import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, AlertTriangle, TrendingDown, CheckCircle, X } from 'lucide-react';
import api from '../api/axios';

const NotificationBell = () => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [insights, setInsights] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    fetchInsights();
    const interval = setInterval(fetchInsights, 300000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchInsights = async () => {
    try {
      const res = await api.get('/ai/insights');
      if (res.data?.insights) {
        const criticalAndWarnings = res.data.insights.filter(
          i => i.severity === 'critical' || i.severity === 'warning'
        );
        setInsights(criticalAndWarnings);
        setUnreadCount(criticalAndWarnings.length);
      }
    } catch (err) {}
  };

  const getIcon = (severity) => {
    if (severity === 'critical') return <AlertTriangle size={14} className="text-red-400" />;
    if (severity === 'warning') return <TrendingDown size={14} className="text-yellow-400" />;
    return <CheckCircle size={14} className="text-emerald-400" />;
  };

  const notifTitle = i18n.language === 'sw' ? 'Arifa' : 'Notifications';
  const allGood = i18n.language === 'sw' ? 'Hakuna arifa!' : 'All good! No alerts.';
  const viewAll = i18n.language === 'sw' ? 'Ona Zote' : 'View All Insights';

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)}
        className="p-2 md:p-2.5 hover:bg-white/5 rounded-lg md:rounded-xl transition text-gray-400 hover:text-white relative">
        <Bell size={16} className="md:size-[18px]" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-2 w-80 bg-[#111a14] border border-white/10 rounded-xl shadow-2xl z-50">
          <div className="p-3 border-b border-white/5 flex items-center justify-between">
            <h3 className="text-white font-medium text-sm">{notifTitle}</h3>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-white"><X size={14} /></button>
          </div>

          {insights.length === 0 ? (
            <div className="p-6 text-center">
              <CheckCircle size={32} className="text-emerald-400 mx-auto mb-2" />
              <p className="text-gray-400 text-sm">{allGood}</p>
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              {insights.map((insight, i) => (
                <button key={i} onClick={() => { setOpen(false); navigate('/ai-insights'); }}
                  className="w-full p-3 text-left hover:bg-white/5 transition border-b border-white/5 last:border-0">
                  <div className="flex items-start gap-2">
                    <div className="mt-0.5">{getIcon(insight.severity)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-300 text-xs line-clamp-2">
                        {i18n.language === 'sw' ? insight.text_sw : insight.text_en}
                      </p>
                      <p className="text-gray-600 text-[10px] mt-1 capitalize">{insight.severity}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          <button onClick={() => { setOpen(false); navigate('/ai-insights'); }}
            className="w-full p-3 text-center text-emerald-400 text-xs font-medium hover:bg-emerald-500/5 transition border-t border-white/5">
            {viewAll}
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
